import classNames from 'classnames'
import { Layer } from '@dustin-web/styledust/constants'
import React, { MutableRefObject, useEffect, useRef, useState, type JSX } from 'react'
import { useScrollPosition, useResponsive } from '../hooks'
import { Portal } from '../portal'
import ConditionalWrapper from '../utils/conditional-wrapper'
import { defaultPositions, getValidPosition, PositionType } from './renderer'
import { getLayerClassName } from '../utils/get-layer-class-name'

type Position = {
  top?: number
  left?: number
}

export type Theme = 'light-blue' | 'light-purple' | 'light-brown' | null

type Props = {
  tooltip?: boolean
  positions?: PositionType[]
  overrideBestPlacement?: boolean
  positionOffset?: Position
  content: string | JSX.Element
  open: boolean
  onToggle: (arg0: boolean) => void
  closeBtn?: boolean
  closeOnOutsideClick?: boolean
  closeOnEscape?: boolean
  toggleOnHover?: boolean
  children?: React.ReactNode
  targetRef?: MutableRefObject<HTMLElement | null>
  className?: string
  popoverStyle?: React.CSSProperties
  popoverClassName?: string
  popoverLayerClassName?: string
  theme?: Theme
  style?: React.CSSProperties
  portal?: boolean
  layer?: Layer
}

export const Popover = ({
  tooltip = false,
  positions = tooltip ? defaultPositions : [],
  overrideBestPlacement = false,
  positionOffset,
  closeBtn = true,
  content,
  open = false,
  onToggle,
  closeOnOutsideClick = false,
  closeOnEscape = false,
  toggleOnHover = tooltip ? true : false,
  children,
  targetRef,
  className = '',
  popoverStyle = {},
  popoverClassName = '',
  popoverLayerClassName = '',
  theme = null,
  style,
  portal = true,
  layer = 'dialog',
}: Props) => {
  const [positionClass, setPositionClass] = useState('')
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties | null>(null)
  const delayHandler = useRef<number>(0)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { windowSize: viewportSize } = useResponsive()
  const layerClassName = getLayerClassName(layer)

  const closePopover = () => {
    targetRef?.current?.focus()
    onToggle(false)
  }

  const handleMouseEnter = () => {
    delayHandler.current = window.setTimeout(() => {
      if (toggleOnHover) {
        onToggle(true)
      }
    }, 500)
  }

  const handleMouseLeave = () => {
    clearTimeout(delayHandler.current)

    if (open && toggleOnHover) {
      onToggle(false)
    }
  }

  useEffect(() => {
    if (!closeOnEscape) return

    const handleKeyUp = (e: KeyboardEvent): void => {
      switch (e.key) {
        case 'Escape':
          onToggle(false)
          break
        default:
          break
      }
    }

    document.addEventListener('keyup', handleKeyUp)
    return () => {
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  useEffect(() => {
    if (!targetRef || !targetRef?.current) {
      return
    }

    const el = targetRef.current
    el.addEventListener('mouseenter', handleMouseEnter)
    el.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      el.removeEventListener('mouseenter', handleMouseEnter)
      el.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [targetRef])

  // for tooltip component
  useEffect(() => {
    if (!targetRef || !targetRef?.current) {
      return
    }

    const el = targetRef.current
    if (!tooltip || !(el.tagName === 'BUTTON' || el.tagName === 'A')) {
      return
    }

    const handleFocus = () => {
      onToggle(true)
    }

    const handleBlur = () => {
      onToggle(false)
    }

    const handleClick = () => {
      onToggle(true)
    }

    el.addEventListener('focus', handleFocus)
    el.addEventListener('blur', handleBlur)
    el.addEventListener('click', handleClick)

    return () => {
      el.removeEventListener('focus', handleFocus)
      el.removeEventListener('blur', handleBlur)
      el.removeEventListener('click', handleClick)
    }
  }, [targetRef])

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popoverRef?.current && !popoverRef?.current.contains(event.target)) {
        onToggle(false)
      }
    }

    if (closeOnOutsideClick && open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const updatePosition = () => {
    if (!open || !popoverRef?.current) {
      return
    }

    const {
      position,
      arrowStyle: newArrowPosition,
      popoverStyle: newPopoverStyle,
      validPosition,
    } = getValidPosition({
      popoverRef,
      containerRef: targetRef ?? containerRef,
      positions,
      overrideBestPlacement,
      positionOffset,
      windowSize: viewportSize,
    })

    const popoverEl = popoverRef.current

    popoverEl.style.position = 'fixed'
    popoverEl.style.top = `${position?.top}px`
    popoverEl.style.left = `${position?.left}px`

    if (newPopoverStyle?.width) {
      popoverEl.style.width = newPopoverStyle.width.toString()
    }

    popoverEl.focus()

    setPositionClass(`c-popover--${validPosition}`)

    setArrowStyle(newArrowPosition)
  }

  useEffect(() => {
    updatePosition()
  }, [open, targetRef, containerRef, popoverRef, viewportSize])

  useScrollPosition(() => {
    updatePosition()
  })

  // return container
  return (
    <>
      {!targetRef && (
        <span
          ref={containerRef}
          className={classNames('c-popover-wrapper', className)}
          style={style}
          onMouseEnter={() => (toggleOnHover ? handleMouseEnter() : null)}
          onMouseLeave={() => (toggleOnHover ? handleMouseLeave() : null)}>
          {children}
        </span>
      )}
      {open && (
        <>
          <ConditionalWrapper
            condition={portal}
            wrapper={children => (
              <Portal
                className={`u-relative ${popoverLayerClassName} ${layerClassName}`}
                refCallback={() => {
                  setTimeout(updatePosition, 100)
                }}>
                {children}
              </Portal>
            )}>
            <div
              aria-modal
              role="dialog"
              tabIndex={-1}
              ref={popoverRef}
              className={classNames(`c-popover`, positionClass, popoverClassName, {
                'c-popover--w-close-btn': closeBtn && !tooltip,
                'c-popover--tooltip': tooltip,
                [`c-popover--${theme}`]: theme,
                'c-popover--custom-arrow': !!arrowStyle,
                [layerClassName]: !portal,
              })}
              style={{ ...popoverStyle }}>
              {closeBtn && !tooltip && (
                <button
                  className="c-close-btn"
                  aria-label="close popover"
                  onClick={closePopover}></button>
              )}
              {content}
              {arrowStyle ? (
                <div className="c-popover__arrow" style={{ ...arrowStyle }}></div>
              ) : null}
            </div>
          </ConditionalWrapper>
        </>
      )}
    </>
  )
}

export default Popover
